import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// post-client-mod -- 文章编辑器（新建/编辑复用）
//
// 支持图片上传：工具栏按钮选择、拖拽到编辑区、粘贴剪贴板图片。
// ============================================================================
import * as React from 'react';
import { getPostApiClient } from '../post-api-client.js';
import { useCreatePost } from '../hooks/use-create-post.js';
import { useUpdatePost } from '../hooks/use-update-post.js';
import { useCategories } from '../hooks/use-categories.js';
import { useTags } from '../hooks/use-tags.js';
/**
 * 文章编辑器组件
 *
 * 支持两种模式：
 * - 新建模式：post 不传，表单为空
 * - 编辑模式：传入 post，表单预填数据
 */
export function PostEditor({ post, onSuccess, onCancel }) {
    const [title, setTitle] = React.useState(post?.title ?? '');
    const [content, setContent] = React.useState(post?.content ?? '');
    const [status, setStatus] = React.useState(post?.status === 'archived' ? 'draft' : (post?.status ?? 'draft'));
    const [categorySlug, setCategorySlug] = React.useState(post?.categorySlug ?? '');
    const [tagSlugs, setTagSlugs] = React.useState(post?.tagSlugs ?? []);
    const [tagInput, setTagInput] = React.useState('');
    const [uploading, setUploading] = React.useState(false);
    const [uploadError, setUploadError] = React.useState(null);
    const [isDragOver, setIsDragOver] = React.useState(false);
    const { creating, error: createError, createPost } = useCreatePost();
    const { updating, error: updateError, updatePost } = useUpdatePost();
    const { categories } = useCategories();
    const { tags: availableTags } = useTags();
    const textareaRef = React.useRef(null);
    const fileInputRef = React.useRef(null);
    const isEditMode = !!post;
    const submitting = creating || updating;
    const error = uploadError ?? createError ?? updateError;
    // ========================================================================
    // 图片上传逻辑
    // ========================================================================
    const handleUploadFile = async (file) => {
        if (!file.type.startsWith('image/')) {
            setUploadError('请选择图片文件（PNG、JPG、GIF、WebP、SVG）');
            return;
        }
        setUploading(true);
        setUploadError(null);
        try {
            const { url } = await getPostApiClient().uploadImage(file);
            const altText = file.name.replace(/\.[^.]+$/, '');
            insertMarkdownAtCursor(`![${altText}](${url})`);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : '图片上传失败';
            setUploadError(message);
        }
        finally {
            setUploading(false);
        }
    };
    /** 在 textarea 光标位置插入文本 */
    const insertMarkdownAtCursor = (text) => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newContent = content.substring(0, start) + text + content.substring(end);
        setContent(newContent);
        // 恢复光标位置到插入文本之后
        requestAnimationFrame(() => {
            textarea.selectionStart = textarea.selectionEnd = start + text.length;
            textarea.focus();
        });
    };
    /** 处理文件选择 */
    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleUploadFile(file);
        }
        // 重置 input 以便重复选择同一文件
        e.target.value = '';
    };
    /** 处理拖拽进入 */
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };
    /** 处理拖拽悬停 */
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    /** 处理拖拽离开 */
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };
    /** 处理拖放 */
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                handleUploadFile(file);
            }
            else {
                setUploadError('拖入的文件必须是图片格式');
            }
        }
    };
    /** 处理粘贴（支持从剪贴板粘贴截图） */
    const handlePaste = (e) => {
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                e.preventDefault();
                const file = items[i].getAsFile();
                if (file) {
                    handleUploadFile(file);
                }
                return;
            }
        }
    };
    // ========================================================================
    // 表单提交
    // ========================================================================
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim() || !content.trim()) {
            return;
        }
        let result = null;
        if (isEditMode && post) {
            const params = {};
            if (title !== post.title)
                params.title = title;
            if (content !== post.content)
                params.content = content;
            if (status !== post.status)
                params.status = status;
            if (categorySlug !== (post.categorySlug ?? ''))
                params.categorySlug = categorySlug || undefined;
            if (JSON.stringify(tagSlugs) !== JSON.stringify(post.tagSlugs))
                params.tagSlugs = tagSlugs;
            if (Object.keys(params).length === 0) {
                onSuccess?.(post);
                return;
            }
            result = await updatePost(post.id, params);
        }
        else {
            const params = {
                title: title.trim(),
                content: content.trim(),
                status,
            };
            if (categorySlug)
                params.categorySlug = categorySlug;
            if (tagSlugs.length > 0)
                params.tagSlugs = tagSlugs;
            result = await createPost(params);
        }
        if (result) {
            onSuccess?.(result);
        }
    };
    const handleAddTag = () => {
        const trimmed = tagInput.trim().toLowerCase();
        if (trimmed && !tagSlugs.includes(trimmed)) {
            setTagSlugs([...tagSlugs, trimmed]);
        }
        setTagInput('');
    };
    const handleRemoveTag = (slug) => {
        setTagSlugs(tagSlugs.filter(t => t !== slug));
    };
    const handleTagInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };
    // 处理从已有标签中快速添加
    const handleQuickAddTag = (slug) => {
        if (!tagSlugs.includes(slug)) {
            setTagSlugs([...tagSlugs, slug]);
        }
    };
    return (_jsxs("form", { onSubmit: handleSubmit, style: styles.form, children: [_jsx("input", { ref: fileInputRef, type: "file", accept: "image/*", onChange: handleFileSelect, style: { display: 'none' } }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "\u6807\u9898" }), _jsx("input", { type: "text", value: title, onChange: (e) => setTitle(e.target.value), placeholder: "\u8BF7\u8F93\u5165\u6587\u7AE0\u6807\u9898", required: true, style: styles.input })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "\u5206\u7C7B" }), _jsxs("select", { value: categorySlug, onChange: (e) => setCategorySlug(e.target.value), style: styles.input, children: [_jsx("option", { value: "", children: "\u672A\u5206\u7C7B" }), categories.map(cat => (_jsx("option", { value: cat.slug, children: cat.name }, cat.slug)))] })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "\u6807\u7B7E" }), _jsxs("div", { style: styles.tagInputRow, children: [_jsx("input", { type: "text", value: tagInput, onChange: (e) => setTagInput(e.target.value), onKeyDown: handleTagInputKeyDown, placeholder: "\u8F93\u5165\u6807\u7B7E\u540D\u540E\u56DE\u8F66\u6DFB\u52A0", style: { ...styles.input, flex: 1 } }), _jsx("button", { type: "button", onClick: handleAddTag, disabled: !tagInput.trim(), style: styles.addTagButton, children: "\u6DFB\u52A0" })] }), tagSlugs.length > 0 && (_jsx("div", { style: styles.selectedTags, children: tagSlugs.map(slug => (_jsxs("span", { style: styles.tagChip, children: [_jsx("span", { style: styles.tagChipText, children: slug }), _jsx("button", { type: "button", onClick: () => handleRemoveTag(slug), style: styles.tagChipRemove, children: "\u00D7" })] }, slug))) })), availableTags.length > 0 && (_jsxs("div", { style: styles.quickTags, children: [_jsx("span", { style: styles.quickTagsLabel, children: "\u5DF2\u6709\u6807\u7B7E\uFF1A" }), availableTags
                                .filter(t => !tagSlugs.includes(t.slug))
                                .slice(0, 8)
                                .map(tag => (_jsx("button", { type: "button", onClick: () => handleQuickAddTag(tag.slug), style: styles.quickTagButton, children: tag.name }, tag.slug)))] }))] }), _jsxs("div", { style: styles.field, children: [_jsxs("div", { style: styles.contentLabelRow, children: [_jsx("label", { style: styles.label, children: "\u5185\u5BB9" }), _jsx("div", { style: styles.toolbar, children: _jsxs("button", { type: "button", onClick: () => fileInputRef.current?.click(), disabled: uploading, style: styles.toolbarButton, title: "\u4E0A\u4F20\u56FE\u7247", children: [uploading ? '⏳' : '📷', " ", uploading ? '上传中...' : '图片'] }) })] }), _jsxs("div", { style: {
                            ...styles.textareaWrapper,
                            ...(isDragOver ? styles.textareaWrapperDragOver : {}),
                        }, onDragEnter: handleDragEnter, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, children: [_jsx("textarea", { ref: textareaRef, value: content, onChange: (e) => setContent(e.target.value), onPaste: handlePaste, placeholder: "\u8BF7\u8F93\u5165\u6587\u7AE0\u5185\u5BB9\uFF08\u652F\u6301 Markdown\uFF09\n\n\u63D0\u793A\uFF1A\n- \u70B9\u51FB\u300C\u56FE\u7247\u300D\u6309\u94AE\u4E0A\u4F20\u56FE\u7247\n- \u62D6\u62FD\u56FE\u7247\u5230\u7F16\u8F91\u533A\u4E0A\u4F20\n- \u7C98\u8D34\u526A\u8D34\u677F\u622A\u56FE\uFF08Ctrl+V\uFF09", required: true, rows: 12, style: { ...styles.input, ...styles.textarea } }), isDragOver && (_jsx("div", { style: styles.dropOverlay, children: _jsx("span", { style: styles.dropOverlayText, children: "\u91CA\u653E\u4EE5\u4E0A\u4F20\u56FE\u7247" }) }))] })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "\u72B6\u6001" }), _jsxs("select", { value: status, onChange: (e) => setStatus(e.target.value), style: styles.input, children: [_jsx("option", { value: "draft", children: "\u8349\u7A3F" }), _jsx("option", { value: "published", children: "\u53D1\u5E03" })] })] }), error && _jsx("div", { style: styles.error, children: error }), _jsxs("div", { style: styles.actions, children: [_jsx("button", { type: "button", onClick: onCancel, disabled: submitting, style: styles.cancelButton, children: "\u53D6\u6D88" }), _jsx("button", { type: "submit", disabled: submitting || !title.trim() || !content.trim(), style: styles.submitButton, children: isEditMode
                            ? (updating ? '保存中...' : '保存修改')
                            : (creating ? '创建中...' : '创建文章') })] })] }));
}
const styles = {
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    label: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#374151',
    },
    input: {
        padding: '8px 12px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '14px',
        outline: 'none',
        fontFamily: 'inherit',
    },
    textarea: {
        resize: 'vertical',
        minHeight: '200px',
        lineHeight: '1.6',
    },
    textareaWrapper: {
        position: 'relative',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        transition: 'border-color 0.2s',
    },
    textareaWrapperDragOver: {
        borderColor: '#6366f1',
        backgroundColor: '#f5f3ff',
    },
    dropOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        borderRadius: '6px',
        pointerEvents: 'none',
    },
    dropOverlayText: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#6366f1',
        padding: '12px 24px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '2px dashed #6366f1',
    },
    contentLabelRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    toolbar: {
        display: 'flex',
        gap: '4px',
    },
    toolbarButton: {
        padding: '4px 10px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        backgroundColor: '#f9fafb',
        color: '#374151',
        fontSize: '12px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        whiteSpace: 'nowrap',
    },
    error: {
        padding: '8px 12px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        fontSize: '13px',
        color: '#dc2626',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
        marginTop: '8px',
    },
    cancelButton: {
        padding: '8px 16px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        color: '#374151',
        fontSize: '14px',
        cursor: 'pointer',
    },
    submitButton: {
        padding: '8px 16px',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    // 标签相关样式
    tagInputRow: {
        display: 'flex',
        gap: '8px',
    },
    addTagButton: {
        padding: '8px 12px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        backgroundColor: '#f9fafb',
        color: '#374151',
        fontSize: '13px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    selectedTags: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: '6px',
    },
    tagChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        backgroundColor: '#eef2ff',
        borderRadius: '12px',
        border: '1px solid #c7d2fe',
    },
    tagChipText: {
        fontSize: '12px',
        color: '#4338ca',
    },
    tagChipRemove: {
        background: 'none',
        border: 'none',
        color: '#818cf8',
        cursor: 'pointer',
        fontSize: '14px',
        padding: '0 2px',
        lineHeight: 1,
    },
    quickTags: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '4px',
        marginTop: '6px',
    },
    quickTagsLabel: {
        fontSize: '12px',
        color: '#9ca3af',
    },
    quickTagButton: {
        padding: '2px 8px',
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        backgroundColor: '#ffffff',
        color: '#6b7280',
        fontSize: '11px',
        cursor: 'pointer',
    },
};
//# sourceMappingURL=post-editor.js.map